package com.algorix.web

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.remember
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Article
import org.jetbrains.compose.web.dom.B
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Footer
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.H4
import org.jetbrains.compose.web.dom.Header
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Main
import org.jetbrains.compose.web.dom.Nav
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Section
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul

private const val SCANME_SUPPORT = "https://passmanagerapp.github.io/scanme-support/"
private const val SCANME_PRIVACY = "/privacy/scanme/"
private const val SCANME_APP_STORE = "https://apps.apple.com/app/id6814827632"
private const val SCANME_PLAY = "https://play.google.com/store/apps/details?id=com.akilincarslan.scanme"
private const val MATH_PLAY = "https://play.google.com/store/apps/details?id=com.akilincarslan.mathonly"
private const val MATH_APP_STORE = "https://apps.apple.com/app/id6816160544"
private const val VAULT_PLAY = "https://play.google.com/store/apps/details?id=com.akilincarslan.passmanager"

@Composable
fun App() {
    val carousel = remember { Carousel(SLIDES) }
    val depth = remember { Depth() }
    val room = remember { Room() }

    val page = remember { Page.current() }

    A(href = "#main", attrs = { classes("skip") }) { Text(t("skip")) }
    Field(room)
    TopBar(room, page)
    Div({ classes("page") }) {
        Main({ id("main") }) {
            when (page) {
                Page.HOME -> {
                    Hero(carousel, depth)
                    Apps(depth)
                    Approach(depth)
                    About(depth)
                }
                Page.COMMERCE -> CommercePage(depth)
            }
        }
        SiteFooter(page)
    }

    // Runs after every element above has registered its ref.
    DisposableEffect(Unit) {
        carousel.start()
        depth.start()
        room.start()
        onDispose { }
    }
}

@Composable
private fun Field(room: Room) {
    Div({ classes("field"); aria("hidden", "true") }) {
        for (b in 1..4) Span({ classes("blob", "b$b") })
        for (i in 0..1) {
            Span({
                classes("tint")
                if (room.tintOn == i) classes("on")
                val z = room.tintZone[i]
                if (z.isNotEmpty()) style { property("--c", "var(--t-$z)") }
            })
        }
        Span({ classes("vig") })
    }
}

@Composable
private fun TopBar(room: Room, page: Page) {
    Header({ classes("top") }) {
        Div({ classes("top-in") }) {
            A(href = page.home("#top"), attrs = { classes("brand", "pill"); aria("label", t("brand.aria")) }) {
                Img(src = "/assets/algorix-logo.png", alt = "") {
                    attr("width", "420"); attr("height", "96"); attr("decoding", "async")
                }
            }
            Nav({ classes("nav", "pill"); aria("label", t("nav.aria")) }) {
                for (key in listOf("apps", "ecommerce", "approach", "about")) {
                    val href = if (key == "ecommerce") Page.COMMERCE_PATH else page.home("#$key")
                    val current = if (page == Page.COMMERCE) key == "ecommerce" else room.current == key
                    A(href = href, attrs = {
                        attr("data-nav", key)
                        if (current) aria("current", "true")
                    }) { Tx("nav.$key") }
                }
            }
            Div({ classes("lang", "pill"); attr("role", "group"); aria("label", t("lang.aria")) }) {
                for (l in Lang.entries) {
                    Button({
                        attr("type", "button")
                        attr("data-lang", l.code)
                        attr("lang", l.code)
                        aria("pressed", (I18n.lang == l).toString())
                        onClick { I18n.choose(l) }
                    }) { Text(l.code.uppercase()) }
                }
            }
        }
    }
}

@Composable
private fun Hero(carousel: Carousel, depth: Depth) {
    Section({
        classes("hero", "section")
        id("top")
        aria("labelledby", "hero-title")
        attr("data-zone", "")
        attr("data-nav", "")
        depth.hp?.let { style { property("--hp", it.fx(4)) } }
        ref { depth.hero = it; onDispose { depth.hero = null } }
    }) {
        Div({ classes("hero-copy") }) {
            Div({ classes("hc-a") }) {
                P({ classes("eyebrow") }) { Tx("hero.eyebrow") }
                H1({ id("hero-title") }) {
                    Span { Tx("hero.l1") }
                    Span({ classes("mute") }) { Tx("hero.l2") }
                    Span({ classes("mute") }) { Tx("hero.l3") }
                }
            }
            Div({ classes("hc-b") }) {
                P({ classes("lede") }) { Tx("hero.lede") }
                Div({ classes("ctas") }) {
                    A(href = "#apps", attrs = { classes("btn", "btn-solid") }) {
                        Span { Tx("hero.cta1") }
                        Glyph("0 0 16 16", "M8 3v10M3.5 8.5 8 13l4.5-4.5")
                    }
                    A(href = "#approach", attrs = { classes("btn", "btn-glass") }) { Tx("hero.cta2") }
                }
            }
        }
        Stage(carousel)
        P({ classes("sr-only"); id("car-hint") }) { Tx("car.hint") }
        Dock(carousel)
    }
}

@Composable
private fun Stage(c: Carousel) {
    Div({
        classes("stage")
        if (c.dragging) classes("is-dragging")
        id("stage")
        attr("role", "region")
        aria("roledescription", t("car.role"))
        aria("label", t("car.aria"))
        attr("tabindex", "0")
        aria("describedby", "car-hint")
        ref { c.stage = it; onDispose { c.stage = null } }
    }) {
        Div({ classes("floor"); aria("hidden", "true") })
        Div({
            classes("ring")
            id("ring")
            c.k?.let { style { property("--k", it.fx(4)) } }
            ref { c.ring = it; onDispose { c.ring = null } }
        }) {
            SLIDES.forEachIndexed { i, slide ->
                Figure({
                    classes("item")
                    if (c.front == i) classes("is-front")
                    if (i in c.near) classes("is-near")
                    attr("role", "group")
                    aria("roledescription", t("car.slideRole"))
                    aria("label", t("car.slide").replace("{n}", "${i + 1}").replace("{t}", "${c.n}"))
                    style {
                        property("--s", c.s[i].fx(4))
                        property("--c", c.c[i].fx(4))
                        property("--d", c.d[i].fx(3))
                    }
                    ref { c.items[i] = it; onDispose { c.items[i] = null } }
                }) {
                    Device(slide.ios, slide.srcEn, slide.srcTr, slide.alt, lazy = false, priority = i == 0, fog = true)
                }
            }
        }
    }
}

private fun pad(n: Int) = if (n < 10) "0$n" else n.toString()

@Composable
private fun Dock(c: Carousel) {
    val slide = SLIDES[c.front]
    val app = APPS.getValue(slide.app)
    Div({ classes("dock"); id("dock"); ref { c.dock = it; onDispose { c.dock = null } } }) {
        Button({
            classes("dk-btn"); attr("type", "button"); id("car-prev")
            aria("label", t("car.prev")); aria("controls", "ring")
            onClick { c.prev() }
        }) { Glyph("0 0 18 18", "M11 4 6 9l5 5") }
        Div({ classes("dk-live"); id("cap-live"); aria("live", if (c.liveOn) "polite" else "off"); aria("atomic", "true") }) {
            Span({ classes("dk-icons"); aria("hidden", "true") }) {
                for ((key, info) in APPS) {
                    Img(src = info.icon, alt = "") {
                        classes("dk-icon")
                        if (key == slide.app) classes("is-on")
                        attr("data-app", key)
                        attr("width", "192"); attr("height", "192"); attr("decoding", "async")
                    }
                }
            }
            Div({ classes("dk-text"); id("cap-text"); ref { c.capText = it; onDispose { c.capText = null } } }) {
                P({ classes("dk-line") }) {
                    Span({ classes("dk-name"); id("cap-name") }) { Tx(app.name) }
                    Span({ classes("dk-cat"); id("cap-cat") }) { Tx(app.cat) }
                }
                P({ classes("dk-status") }) {
                    Span({
                        classes("st-dot")
                        if (app.dot.isNotEmpty()) classes(app.dot)
                        id("cap-dot"); aria("hidden", "true")
                    })
                    Span({ id("cap-status") }) { Tx(app.st) }
                }
            }
            P({ classes("dk-screen"); id("cap-screen"); ref { c.capScreen = it; onDispose { c.capScreen = null } } }) { Tx(slide.cap) }
        }
        P({ classes("dk-count"); aria("hidden", "true") }) {
            B({ id("cap-idx") }) { Text(pad(c.front + 1)) }
            Text(" / ${pad(c.n)}")
        }
        Button({
            classes("dk-btn"); attr("type", "button"); id("car-toggle")
            attr("data-state", if (c.playing) "playing" else "paused")
            aria("label", t(if (c.playing) "car.pause" else "car.play")); aria("controls", "ring")
            if (!c.canPause) attr("hidden", "")
            onClick { c.togglePlay() }
        }) {
            Glyph("0 0 18 18", "M6.5 4.5v9M11.5 4.5v9", cls = "i-pause", round = false)
            Glyph("0 0 18 18", "M6.5 4.6v8.8L13.4 9z", cls = "i-play", filled = true)
        }
        Button({
            classes("dk-btn"); attr("type", "button"); id("car-next")
            aria("label", t("car.next")); aria("controls", "ring")
            onClick { c.next() }
        }) { Glyph("0 0 18 18", "m7 4 5 5-5 5") }
    }
}

// ---------- apps ----------

private class Shot(val ios: Boolean, val en: String, val tr: String?, val alt: String)

@Composable
private fun Apps(depth: Depth) {
    Section({ classes("section"); id("apps"); aria("labelledby", "apps-title") }) {
        Div({ classes("wrap", "apps-head"); attr("data-zone", ""); attr("data-nav", "apps") }) {
            H2({ classes("h2"); id("apps-title") }) { Tx("apps.title") }
            P({ classes("sec-lede") }) { Tx("apps.lede") }
            Ul({ classes("app-index"); aria("label", t("idx.aria")) }) {
                IndexLink("#scanme", "/assets/scanme.png", null, "ScanMe", "cat.scanme")
                IndexLink("#basic-math", "/assets/basic-math.png", "name.math", null, "cat.math")
                IndexLink("#vaultly", "/assets/vaultly.png", null, "Vaultly", "cat.vault")
            }
        }

        Scene(
            depth.vols[0], id = "scanme", titleId = "scanme-title", zone = "scan", flip = false,
            icon = "/assets/scanme.png", iconAlt = "scanme.icon", name = { Text("ScanMe") }, catKey = "cat.scanme",
            desc = { Tx("scanme.desc") },
            feats = listOf("scanme.f1", "scanme.f2", "scanme.f3", "scanme.f4"),
            chips = listOf("scanme.m1", "scanme.m2"),
            stores = {
                StoreLive(Store.APP_STORE, SCANME_APP_STORE)
                StoreLive(Store.GOOGLE_PLAY, SCANME_PLAY)
            },
            links = {
                P({ classes("w-links") }) {
                    ExternalLink(SCANME_SUPPORT) { Span { Tx("scanme.support") } }
                    ExternalLink(SCANME_PRIVACY) { Span { Tx("privacy.link") } }
                }
            },
            shots = listOf(
                Shot(true, "/assets/scanme-en-08-wifi-creator.jpg", "/assets/scanme-tr-08-wifi-creator.jpg", "sh.s08"),
                Shot(true, "/assets/scanme-en-10-link-result-dark.jpg", "/assets/scanme-tr-10-link-result-dark.jpg", "sh.s10"),
                Shot(true, "/assets/scanme-en-02-history.jpg", "/assets/scanme-tr-02-history.jpg", "sh.s02"),
            ),
        )

        Scene(
            depth.vols[1], id = "basic-math", titleId = "math-title", zone = "math", flip = true,
            icon = "/assets/basic-math.png", iconAlt = "math.icon", name = { Tx("name.math") }, catKey = "math.cat",
            desc = { Tx("math.desc") },
            feats = listOf("math.f1", "math.f2", "math.f3", "math.f4"),
            chips = emptyList(),
            stores = {
                StoreLive(Store.APP_STORE, MATH_APP_STORE)
                StoreLive(Store.GOOGLE_PLAY, MATH_PLAY)
            },
            links = {},
            shots = listOf(
                Shot(true, "/assets/basic-math-en-03-quiz.jpg", "/assets/basic-math-tr-03-quiz.jpg", "sh.m03"),
                Shot(true, "/assets/basic-math-en-04-teacher.jpg", "/assets/basic-math-tr-04-teacher.jpg", "sh.m04"),
                Shot(true, "/assets/basic-math-en-01-home.jpg", "/assets/basic-math-tr-01-home.jpg", "sh.m01"),
            ),
        )

        Scene(
            depth.vols[2], id = "vaultly", titleId = "vault-title", zone = "vault", flip = false,
            icon = "/assets/vaultly.png", iconAlt = "vault.icon", name = { Text("Vaultly") }, catKey = "cat.vault",
            desc = { RichText("vault.desc") },
            feats = listOf("vault.f1", "vault.f2", "vault.f3", "vault.f4"),
            chips = listOf("vault.m1", "vault.m2", "vault.m3"),
            stores = {
                StoreLive(Store.GOOGLE_PLAY, VAULT_PLAY)
                StoreSoon(Store.APP_STORE)
            },
            links = {},
            shots = listOf(
                Shot(false, "/assets/vaultly-en-04-add.jpg", null, "sh.v04"),
                Shot(false, "/assets/vaultly-en-05-settings.jpg", null, "sh.v05"),
                Shot(false, "/assets/vaultly-en-02-item.jpg", null, "sh.v02"),
            ),
        )
    }
}

@Composable
private fun IndexLink(href: String, icon: String, nameKey: String?, name: String?, catKey: String) {
    Li {
        A(href = href) {
            Img(src = icon, alt = "") {
                attr("width", "192"); attr("height", "192"); attr("loading", "lazy"); attr("decoding", "async")
            }
            Span({ classes("nm") }) { if (nameKey != null) Tx(nameKey) else Text(name ?: "") }
            Span({ classes("ct") }) { Tx(catKey) }
        }
    }
}

@Composable
private fun Scene(
    vol: Vol,
    id: String,
    titleId: String,
    zone: String,
    flip: Boolean,
    icon: String,
    iconAlt: String,
    name: @Composable () -> Unit,
    catKey: String,
    desc: @Composable () -> Unit,
    feats: List<String>,
    chips: List<String>,
    stores: @Composable () -> Unit,
    links: @Composable () -> Unit,
    shots: List<Shot>,
) {
    Article({
        classes("scene")
        if (flip) classes("flip")
        id(id)
        aria("labelledby", titleId)
        attr("data-zone", zone)
        attr("data-nav", "apps")
        vol.asm?.let { style { property("--asm", it.fx(4)) } }
        ref { vol.scene = it; onDispose { vol.scene = null } }
    }) {
        Div({ classes("wrap", "scene-grid") }) {
            Div({
                classes("window")
                vol.wy?.let { style { property("--wy", it.fx(1) + "px") } }
                ref { vol.win = it; onDispose { vol.win = null } }
            }) {
                Div({ classes("w-head") }) {
                    Img(src = icon, alt = t(iconAlt)) {
                        classes("app-icon")
                        attr("width", "192"); attr("height", "192"); attr("loading", "lazy"); attr("decoding", "async")
                    }
                    Div {
                        H3({ id(titleId) }) { name() }
                        P({ classes("w-cat") }) { Tx(catKey) }
                    }
                }
                P({ classes("w-desc") }) { desc() }
                Ul({ classes("feats") }) {
                    for (f in feats) Li {
                        H4 { Tx("${f}t") }
                        P { Tx("${f}d") }
                    }
                }
                if (chips.isNotEmpty()) Ul({ classes("chips") }) {
                    for (ch in chips) Li { Tx(ch) }
                }
                Div({ classes("stores") }) { stores() }
                links()
            }
            Div({
                classes("volume")
                attr("data-vol", "")
                ref { vol.el = it; onDispose { vol.el = null } }
            }) {
                Div({
                    classes("vol-in")
                    style {
                        property("--rx", vol.rx.fx(3) + "deg")
                        property("--ry", vol.ry.fx(3) + "deg")
                    }
                    ref { vol.inner = it; onDispose { vol.inner = null } }
                }) {
                    Span({ classes("vol-glow"); aria("hidden", "true") })
                    shots.forEachIndexed { i, s ->
                        Figure({ classes("fl", "fl-${i + 1}") }) { Device(s.ios, s.en, s.tr, s.alt) }
                    }
                }
            }
        }
    }
}

// ---------- approach ----------

@Composable
private fun Approach(depth: Depth) {
    Section({
        classes("section", "approach"); id("approach"); aria("labelledby", "approach-title")
        attr("data-zone", ""); attr("data-nav", "approach")
    }) {
        Div({ classes("wrap", "ap-grid") }) {
            Div({ classes("ap-copy") }) {
                P({ classes("eyebrow") }) { Tx("approach.eyebrow") }
                H2({ classes("h2"); id("approach-title") }) { Tx("approach.title") }
                P({ classes("sec-lede"); style { property("margin-top", "22px") } }) { Tx("approach.lede") }
                Ul({ classes("ap-points") }) {
                    Point("commonMain", "p1")
                    Point("androidMain · iosMain", "p2")
                    Point("Gradle · Xcode", "p3")
                }
            }
            KmpFigure(depth)
        }
    }
}

@Composable
internal fun Point(key: String, id: String) {
    Li {
        Span({ classes("ap-key") }) { Text(key) }
        H3 { Tx("$id.t") }
        P { RichText("$id.d") }
    }
}

@Composable
private fun KmpFigure(depth: Depth) {
    Figure({
        classes("kmp")
        if (depth.kmp3d) classes("is-3d")
        id("kmp")
        aria("labelledby", "kmp-cap")
        depth.kmpAsm?.let { style { property("--asm", it.fx(4)) } }
        ref { depth.kmp = it; onDispose { depth.kmp = null } }
    }) {
        Div({ classes("kmp-view") }) {
            Div({ classes("board") }) {
                P({ classes("tier", "t1") }) { Tx("fig.t1") }
                Div({ classes("node", "n-shared") }) {
                    P({ classes("nk") }) { Text("shared/commonMain") }
                    P({ classes("nt") }) { Tx("fig.shared") }
                    P({ classes("ns") }) {
                        Span { Tx("fig.ui") }
                        Text(" · ")
                        Span { Tx("fig.logic") }
                        Text(" · Room KMP · Koin")
                    }
                }
                Span({ classes("fork"); aria("hidden", "true") }) {
                    Span({ classes("fork-lbl") }) { Text("expect / actual") }
                }
                P({ classes("tier", "t2") }) { Tx("fig.t2") }
                Node("r2", "la", "androidMain", "fig.and", "CameraX · ML Kit · Android Keystore")
                Node("r2", "lb", "iosMain", "fig.ios", "AVFoundation · Vision · VisionKit · Secure Enclave")
                P({ classes("tier", "t3") }) { Tx("fig.t3") }
                Node("r3", "la", "Gradle", "fig.abuild", null)
                Node("r3", "lb", "Xcode", "fig.ibuild", null)
                P({ classes("tier", "t4") }) { Tx("fig.t4") }
                StoreNode("la", "Android", Store.GOOGLE_PLAY)
                StoreNode("lb", "iPhone", Store.APP_STORE)
            }
        }
        Figcaption({ id("kmp-cap") }) { Tx("fig.cap") }
    }
}

@Composable
internal fun Node(row: String, lane: String, key: String, titleKey: String, sub: String?) {
    Div({ classes("node", row, lane) }) {
        P({ classes("nk") }) { Text(key) }
        P({ classes("nt") }) { Tx(titleKey) }
        if (sub != null) P({ classes("ns") }) { Text(sub) }
    }
}

@Composable
private fun StoreNode(lane: String, key: String, store: Store) {
    Div({ classes("node", "r4", lane, "store-n") }) {
        P({ classes("nk") }) { Text(key) }
        P({ classes("nt") }) {
            StoreIcon(store.icon, "n-ico")
            Text(store.label)
        }
    }
}

// ---------- about ----------

@Composable
private fun About(depth: Depth) {
    Section({
        classes("section", "about"); id("about"); aria("labelledby", "about-title")
        attr("data-zone", ""); attr("data-nav", "about")
    }) {
        Div({ classes("wrap") }) {
            H2({ classes("h2"); id("about-title") }) { Tx("about.title") }
            P({ classes("sec-lede", "ab-lede") }) { Tx("about.lede") }
            Div({
                classes("plates")
                id("plates")
                depth.platesAsm?.let { style { property("--asm", it.fx(4)) } }
                ref { depth.plates = it; onDispose { depth.plates = null } }
            }) {
                Ul({ classes("plate-row") }) {
                    for (p in listOf("a1", "a2", "a3")) Li({ classes("plate") }) {
                        H3 { Tx("$p.t") }
                        P { Tx("$p.d") }
                    }
                }
            }
        }
    }
}

// ---------- footer ----------

@Composable
private fun SiteFooter(page: Page) {
    Footer({ classes("foot"); aria("label", t("footer.aria")) }) {
        Div({ classes("wrap") }) {
            Div({ classes("foot-in") }) {
                Div({ classes("f-brand") }) {
                    A(href = page.home("#top"), attrs = { classes("brand-line"); aria("label", t("brand.aria")) }) {
                        Img(src = "/assets/algorix-logo.png", alt = "") {
                            attr("width", "420"); attr("height", "96"); attr("loading", "lazy"); attr("decoding", "async")
                        }
                    }
                    P { Tx("footer.line") }
                }
                Div {
                    P({ classes("f-h") }) { Tx("footer.apps") }
                    Ul {
                        Li { A(href = page.home("#scanme")) { Text("ScanMe") } }
                        Li { A(href = page.home("#basic-math")) { Tx("name.math") } }
                        Li { A(href = page.home("#vaultly")) { Text("Vaultly") } }
                        Li { A(href = Page.COMMERCE_PATH) { Tx("footer.ecommerce") } }
                    }
                }
                Div {
                    P({ classes("f-h") }) { Tx("footer.support") }
                    Ul {
                        Li { ExternalLink(SCANME_SUPPORT) { Span { Tx("footer.ssupport") } } }
                        Li { ExternalLink(SCANME_PRIVACY) { Span { Tx("footer.sprivacy") } } }
                    }
                }
            }
            Div({ classes("f-legal") }) {
                P { Tx("footer.tm") }
                P { Text("© 2026 AlgoriX") }
            }
        }
    }
}
